import { DropDownButton, DropdownItem, IconName, Stack } from '@optimus-web/core';
import { UserProfileResult } from '@mews-commander/data-access';
import { LocalizedText, useDialog } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { hasCurrentProfilePlatformScope } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { useCommanderSelector } from '@mews-commander/utils';
import { ModalSidePanel } from '@mews-commander/ui';
import { useUserDetailContext } from '../../context/userDetailContext';
import { useTableActionsInfo } from '../../hooks/useTableActionsInfo';
import { useUserProfilesGet } from '../../queries';
import { UserType } from '../../types';
import { useUserContext } from '../../context';
import { handleCopyToClipboard } from '../../utils';
import { DeleteUserButton } from '../DeleteUser';
import { ResetPasswordDialog } from '../ResetPassword';
import { ResetMFADialog, useCanSeeMfaReset } from '../ResetMFA';
import { UserDetailContent } from './UserDetailContent/UserDetailContent';

export const UserDetail = ({ user }: { user: UserProfileResult }) => {
    const { closeUserDetail } = useUserDetailContext();
    const { data, isLoading } = useUserProfilesGet(user.userId);
    const { isActionDisabled } = useTableActionsInfo(user);

    const [mfaDialogProps, toggleMfaDialog] = useDialog();
    const canSeeMfaReset = useCanSeeMfaReset(data);

    const { userType } = useUserContext();

    const [resetPasswordDialogProps, toggleResetPasswordDialog] = useDialog();
    const canSeePasswordReset = useCommanderSelector(hasCurrentProfilePlatformScope);

    return (
        <>
            <ModalSidePanel
                opened
                onClose={closeUserDetail}
                headerActions={
                    <Stack distribution="space-between" spacing="100">
                        <>
                            {isActionDisabled ? null : (
                                <DeleteUserButton userProfile={user} onSuccess={closeUserDetail} />
                            )}
                            {canSeeMfaReset || userType !== UserType.Properties || canSeePasswordReset ? (
                                <DropDownButton icon={IconName.More} variant="tertiary" size="small">
                                    {userType === UserType.Properties ? null : (
                                        <DropdownItem
                                            key="copyId"
                                            label={<LocalizedText textKey={TextKey.CopyProfileId} />}
                                            onClick={() => handleCopyToClipboard(user.enterpriseProfileIds?.[0])}
                                        />
                                    )}
                                    {canSeeMfaReset ? (
                                        <DropdownItem
                                            key="resetMFA"
                                            label={<LocalizedText textKey={TextKey.ResetTwoFactorShort} />}
                                            onClick={toggleMfaDialog}
                                        />
                                    ) : null}
                                    {canSeePasswordReset && data?.email ? (
                                        <DropdownItem
                                            key="resetPassword"
                                            label={<LocalizedText textKey={TextKey.ResetPassword} />}
                                            onClick={toggleResetPasswordDialog}
                                        />
                                    ) : null}
                                </DropDownButton>
                            ) : null}
                        </>
                    </Stack>
                }
                title={data?.fullName}
                width="medium"
                content={data ? <UserDetailContent user={data} userProfile={user} /> : null}
                loading={isLoading}
            />
            {canSeeMfaReset && <ResetMFADialog userId={user.userId} {...mfaDialogProps} />}
            {canSeePasswordReset && data?.email && (
                <ResetPasswordDialog email={data.email} {...resetPasswordDialogProps} />
            )}
        </>
    );
};
